import { Anchor, Badge, Group, Paper, Stack, Text, Timeline, Title } from '@mantine/core'
import { jobApplicationStatusColor } from '../lib/jobApplicationStatusColors'

// #1121 応募詳細(/applications/:id)。ヘッダ(案件名 / 企業名 / 応募日 / 現在ステータス) + 遷移タイムライン + 面談予定。
// 行データは Rails 側で組み立てて props で受け取り、ここでは描画のみ。
// variant: 'expert' = 自分の応募(応募者名は出さない) / 'client' = 自社案件への応募(応募者名を出す)。
// 非媒介(docs/application-tracking.md §2): 内部メモ・遷移者・面談の連絡事項/確定前の場所は props に含まれない前提。
// 辞退ボタンのプレースホルダはここには置かない(柱2-3で追加)。
// 人材(expert)向けには応募済以降の内部進捗を開示しないため(JobApplication::APPLICANT_VISIBLE_STATUS)、
// Rails 側(job_applications/show.html.erb)は statusChanges・interview を渡さない(props はデフォルトの [] / null)。

function Header({ variant, positionTitle, positionHref, companyName, applicantName, appliedAt, status, statusLabel }) {
  const position = positionTitle || <Text c="dimmed" span>(タイトル未設定)</Text>

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <div>
          <Title order={2} fz="h3">
            {positionHref ? <Anchor href={positionHref}>{position}</Anchor> : position}
          </Title>
          <Text fz="sm" c="dimmed">
            {companyName}
            {variant === 'client' ? <> / {applicantName || '(退会ユーザー)'}</> : null}
          </Text>
          <Text fz="sm" c="dimmed" mt={4}>応募日: {appliedAt}</Text>
        </div>
        <Badge color={jobApplicationStatusColor(status)} variant="light" radius="sm" size="lg">
          {statusLabel}
        </Badge>
      </Group>
    </Paper>
  )
}

// 人材向け(/applications)は応募済以降の内部進捗を開示しないため statusChanges を渡さない。
// 渡ってきた行が無ければパネルごと出さない(常に「履歴はありません」と出るのを避ける)。
function HistoryTimeline({ rows }) {
  if (rows.length === 0) return null

  return (
    <Timeline active={rows.length - 1} bulletSize={16} lineWidth={2}>
      {rows.map((row) => (
        <Timeline.Item
          key={row.id}
          title={
            <Group gap="xs" wrap="wrap">
              {row.fromLabel ? (
                <>
                  <Badge color={jobApplicationStatusColor(row.fromStatus)} variant="light" radius="sm">
                    {row.fromLabel}
                  </Badge>
                  <Text fz="sm" c="dimmed" span>→</Text>
                </>
              ) : (
                <Text fz="sm" c="dimmed" span>応募</Text>
              )}
              <Badge color={jobApplicationStatusColor(row.toStatus)} variant="light" radius="sm">
                {row.toLabel}
              </Badge>
            </Group>
          }
        >
          <Text fz="sm" c="dimmed" mt={4}>{row.changedAt}</Text>
        </Timeline.Item>
      ))}
    </Timeline>
  )
}

function InterviewSection({ interview, variant }) {
  if (!interview) return null

  return (
    <Paper withBorder p="md">
      <Title order={3} fz="h4" mb="sm">面談予定</Title>

      {interview.status === 'confirmed' ? (
        <Stack gap={4}>
          <Text fz="sm">日時: {interview.scheduledAt}</Text>
          <Text fz="sm">所要時間: {interview.durationMinutes}分</Text>
          <Text fz="sm">形式: {interview.formatLabel}</Text>
          <Text fz="sm">
            場所 / URL: {interview.location || <Text c="dimmed" span>未設定</Text>}
          </Text>
        </Stack>
      ) : variant === 'expert' ? (
        <Text fz="sm" c="dimmed">
          候補日時が提示されています。下の<Anchor href="#interview-slot-picker">「面談候補日時を選択してください」</Anchor>からご選択ください。
        </Text>
      ) : (
        <Text fz="sm" c="dimmed">候補日時を提示中です。応募者の選択をお待ちください。</Text>
      )}
    </Paper>
  )
}

export default function JobApplicationDetail({
  variant = 'expert',
  positionTitle,
  positionHref,
  companyName,
  applicantName,
  appliedAt,
  status,
  statusLabel,
  statusChanges = [],
  interview = null,
}) {
  return (
    <Stack gap="lg">
      <Header
        variant={variant}
        positionTitle={positionTitle}
        positionHref={positionHref}
        companyName={companyName}
        applicantName={applicantName}
        appliedAt={appliedAt}
        status={status}
        statusLabel={statusLabel}
      />

      <InterviewSection interview={interview} variant={variant} />

      {statusChanges.length > 0 && (
        <Paper withBorder p="md">
          <Title order={3} fz="h4" mb="sm">ステータス遷移</Title>
          <HistoryTimeline rows={statusChanges} />
        </Paper>
      )}
    </Stack>
  )
}
